import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Row,
    Spinner,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import dataApi from '@/shared/services/dataApi';
import engineApi from '@/shared/services/engineApi';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import { useChatPageState } from '@/shared/contexts/ChatPageContext';
import { formatDate, formatRelativeDate } from '@/shared/utils/dateHelpers';
import isSafeUrl from '@/shared/utils/urlHelpers';
import GeoBoundaryMap from '@/components/common/GeoBoundaryMap';
import ShareToCommunityButton from '@/components/common/ShareToCommunityButton';
import TemporalRangeBar from '@/components/common/TemporalRangeBar';
import InfoTooltip from '@/components/common/InfoTooltip';
import SEO from '@/components/common/SEO';

/**
 * Safely render embed code by extracting the iframe src rather than using
 * dangerouslySetInnerHTML. Falls back to nothing if no valid iframe found.
 */
function EmbedPreview({ embedCode }) {
    const parsed = React.useMemo(() => {
        if (!embedCode) return null;
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(embedCode, 'text/html');
            const iframe = doc.querySelector('iframe');
            if (!iframe) return null;
            const src = iframe.getAttribute('src');
            // Only allow http/https URLs
            if (!src || !/^https?:\/\//i.test(src)) return null;
            return {
                src,
                width: iframe.getAttribute('width') || '100%',
                height: iframe.getAttribute('height') || '500',
                title: iframe.getAttribute('title') || 'Embedded content',
            };
        } catch {
            return null;
        }
    }, [embedCode]);

    if (!parsed) return null;

    return (
        <iframe
            src={parsed.src}
            width={parsed.width}
            height={parsed.height}
            title={parsed.title}
            sandbox="allow-scripts"
            loading="lazy"
            referrerPolicy="no-referrer"
        />
    );
}

EmbedPreview.propTypes = {
    embedCode: PropTypes.string,
};

export default function DataProductDetail() {
    const { id } = useParams();
    const { isAdmin } = useAuth();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { openChat } = useChatPageState();

    // Admin action state
    const [validating, setValidating] = useState(false);
    const [indexing, setIndexing] = useState(false);
    const [actionResult, setActionResult] = useState(null);

    // Publish product detail context so Bili knows what the user is viewing
    // focusedDataProduct includes the id for direct queries
    useChatPageEffect(
        {
            page: 'data-product-detail',
            section: 'data-discovery',
            focusedDataProduct: {
                id: product?.id,
                name: product?.name,
                productType: product?.product_type,
            },
            productName: product?.name,
            organization: product?.organization,
            spotlight: product?.spotlight,
            categories: product?.category,
            keywords: product?.keywords,
            productType: product?.product_type,
        },
        [product?.id]
    );

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const data = await dataApi.getProduct(id);
                if (!cancelled) setProduct(data);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const handleValidate = async () => {
        setValidating(true);
        setActionResult(null);
        try {
            const result = await engineApi.post(
                `/admin/validate-product/${id}`
            );
            setActionResult({
                variant: 'success',
                message: `Validation complete: ${result.status} (HTTP ${result.status_code})`,
            });
            // Refresh to show updated status
            const updated = await dataApi.getProduct(id);
            setProduct(updated);
        } catch (err) {
            setActionResult({
                variant: 'danger',
                message: err.message || 'Validation failed',
            });
        } finally {
            setValidating(false);
        }
    };

    const handleIndex = async () => {
        setIndexing(true);
        setActionResult(null);
        try {
            const result = await engineApi.post(`/admin/products/${id}/index`);
            setActionResult({
                variant: 'success',
                message: result.message || 'Re-indexed successfully',
            });
        } catch (err) {
            setActionResult({
                variant: 'danger',
                message: err.message || 'Indexing failed',
            });
        } finally {
            setIndexing(false);
        }
    };

    if (loading) {
        return (
            <section className="page-container bg-light">
                <Container className="py-5 text-center">
                    <Spinner animation="border" variant="primary" />
                    <span className="visually-hidden">Loading...</span>
                </Container>
            </section>
        );
    }

    if (error || !product) {
        return (
            <section className="page-container bg-light">
                <Container className="py-5">
                    <Alert variant="danger">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error || 'Data product not found.'}</p>
                        <Link to="/data" className="btn btn-outline-primary">
                            Back to Discovery
                        </Link>
                    </Alert>
                </Container>
            </section>
        );
    }

    const categories = product.category || [];
    const keywords = product.keywords || [];

    return (
        <section className="page-container bg-light">
            <SEO title={product?.name || 'Data Product'} />
            <Container className="py-4">
                {/* Breadcrumb */}
                <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item">
                            <Link to="/data">Data Discovery</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to="/data?tab=products">Products</Link>
                        </li>
                        <li
                            className="breadcrumb-item active"
                            aria-current="page"
                        >
                            {product.name}
                        </li>
                    </ol>
                </nav>

                <Row className="g-4">
                    {/* Main content */}
                    <Col lg={8}>
                        <h1 className="h3 text-primary fw-bold mb-3">
                            {product.name}
                        </h1>

                        {product.description && (
                            <p className="text-body-secondary fs-5 mb-4">
                                {product.description}
                            </p>
                        )}

                        {/* Geographic & Temporal Coverage */}
                        {(product.geospatial_bound_description ||
                            product.earliest_date ||
                            product.newest_date) && (
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Body>
                                    <Card.Title className="h6 text-primary mb-3">
                                        Coverage
                                    </Card.Title>
                                    {product.geospatial_bound_description && (
                                        <div className="mb-3">
                                            <strong className="text-muted d-block mb-2 small">
                                                Geographic Coverage
                                                <InfoTooltip text="The geographic area this data describes. The map and text below show its boundary." />
                                            </strong>
                                            <GeoBoundaryMap
                                                description={
                                                    product.geospatial_bound_description
                                                }
                                                geojson={
                                                    product.geospatial_bound
                                                }
                                                className="mb-2"
                                            />
                                            <small className="text-muted">
                                                {
                                                    product.geospatial_bound_description
                                                }
                                            </small>
                                        </div>
                                    )}
                                    {(product.earliest_date ||
                                        product.newest_date) && (
                                        <div>
                                            <strong className="text-muted d-block mb-2 small">
                                                Temporal Coverage
                                                <InfoTooltip text="The span of time this data covers, from its earliest record to its most recent. An Ongoing label means it is still being updated." />
                                            </strong>
                                            <TemporalRangeBar
                                                earliestDate={
                                                    product.earliest_date
                                                }
                                                newestDate={product.newest_date}
                                            />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Embed preview (only shown when the target allows framing) */}
                        {product.embed_code && product.embeddable && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-2">
                                    Preview
                                </strong>
                                <div className="border rounded overflow-hidden">
                                    <EmbedPreview
                                        embedCode={product.embed_code}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Categories */}
                        {categories.length > 0 && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    Categories
                                </strong>
                                <div className="d-flex flex-wrap gap-1">
                                    {categories.map((cat) => (
                                        <Badge
                                            key={cat}
                                            bg="primary"
                                            className="fw-normal"
                                        >
                                            {cat}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Keywords */}
                        {keywords.length > 0 && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    Keywords
                                </strong>
                                <div className="d-flex flex-wrap gap-1">
                                    {keywords.map((kw) => (
                                        <Badge
                                            key={kw}
                                            bg="light"
                                            text="dark"
                                            className="fw-normal"
                                        >
                                            {kw}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {product.notes && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    Notes
                                </strong>
                                <p className="text-body-secondary mb-0">
                                    {product.notes}
                                </p>
                            </div>
                        )}

                        {/* Integration notes */}
                        {product.integration_notes && (
                            <div className="mb-4">
                                <strong className="text-muted d-block mb-1">
                                    Integration Notes
                                </strong>
                                <p className="text-body-secondary mb-0">
                                    {product.integration_notes}
                                </p>
                            </div>
                        )}
                    </Col>

                    {/* Sidebar */}
                    <Col lg={4}>
                        {/* Organization card */}
                        {product.organization && (
                            <Card className="border-0 shadow-sm mb-3">
                                <Card.Body>
                                    <Card.Title className="h6 text-primary">
                                        Organization
                                    </Card.Title>
                                    <p className="mb-0 fw-medium">
                                        {product.organization}
                                    </p>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Metadata card */}
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body>
                                <Card.Title className="h6 text-primary mb-3">
                                    Metadata
                                </Card.Title>
                                <dl className="mb-0">
                                    {product.product_type && (
                                        <>
                                            <dt className="small text-muted">
                                                Product Type
                                            </dt>
                                            <dd>
                                                <Badge bg="warning" text="dark">
                                                    {product.product_type}
                                                </Badge>
                                            </dd>
                                        </>
                                    )}
                                    {product.sustainability_pillar && (
                                        <>
                                            <dt className="small text-muted">
                                                Sustainability Pillar
                                            </dt>
                                            <dd className="text-capitalize">
                                                {product.sustainability_pillar}
                                            </dd>
                                        </>
                                    )}
                                    {product.publication_date && (
                                        <>
                                            <dt className="small text-muted">
                                                Publication Date
                                            </dt>
                                            <dd>
                                                {formatDate(
                                                    product.publication_date
                                                )}
                                            </dd>
                                        </>
                                    )}
                                    <dt className="small text-muted">Status</dt>
                                    <dd>
                                        <Badge
                                            bg={
                                                product.status === 'active'
                                                    ? 'success'
                                                    : 'secondary'
                                            }
                                        >
                                            {product.status}
                                        </Badge>
                                    </dd>
                                </dl>
                            </Card.Body>
                        </Card>

                        {/* Indexing & Validation card */}
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body>
                                <Card.Title className="h6 text-primary mb-3">
                                    Indexing &amp; Validation
                                </Card.Title>
                                <dl className="mb-0">
                                    <dt className="small text-muted">
                                        AI Search Index
                                    </dt>
                                    <dd className="small">
                                        {product.opensearch_indexed ? (
                                            <span className="text-success">
                                                Indexed for AI search
                                            </span>
                                        ) : (
                                            <span className="text-warning">
                                                Not indexed
                                            </span>
                                        )}
                                    </dd>
                                    {product.last_validated && (
                                        <>
                                            <dt className="small text-muted">
                                                Last Validated
                                            </dt>
                                            <dd className="small">
                                                {formatRelativeDate(
                                                    product.last_validated
                                                )}
                                            </dd>
                                        </>
                                    )}
                                    {product.created_at && (
                                        <>
                                            <dt className="small text-muted">
                                                Created
                                            </dt>
                                            <dd className="small">
                                                {formatDate(product.created_at)}
                                            </dd>
                                        </>
                                    )}
                                    {product.updated_at && (
                                        <>
                                            <dt className="small text-muted">
                                                Last Updated
                                            </dt>
                                            <dd className="small">
                                                {formatDate(product.updated_at)}
                                            </dd>
                                        </>
                                    )}
                                </dl>
                            </Card.Body>
                        </Card>

                        {/* Links */}
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Body className="d-flex flex-column gap-2">
                                {product.url && isSafeUrl(product.url) && (
                                    <Button
                                        href={product.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="primary"
                                        className="rounded-pill"
                                    >
                                        View Product
                                    </Button>
                                )}
                                <Button
                                    variant="outline-primary"
                                    className="rounded-pill"
                                    onClick={() =>
                                        openChat(
                                            `Tell me about ${product.name}`
                                        )
                                    }
                                >
                                    Ask Bili About This
                                </Button>
                                <ShareToCommunityButton
                                    variant="button"
                                    shareContent={{
                                        postType: 'resource',
                                        title: product.name,
                                        content: product.description,
                                        resourceUrl: product.url,
                                        resourceType:
                                            {
                                                report: 'report',
                                                publication: 'report',
                                                dashboard: 'tool',
                                                api: 'tool',
                                                application: 'tool',
                                                storymap: 'tool',
                                                map_viewer: 'tool',
                                                database: 'dataset',
                                            }[
                                                product.product_type
                                                    ?.toLowerCase()
                                                    .trim()
                                            ] || '',
                                    }}
                                />
                                {product.spotlight && (
                                    <Link
                                        to={`/spotlights/${product.spotlight}`}
                                        className="btn btn-outline-dark rounded-pill"
                                    >
                                        {product.spotlight
                                            .charAt(0)
                                            .toUpperCase() +
                                            product.spotlight.slice(1)}{' '}
                                        Spotlight
                                    </Link>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Admin Actions (conditional) */}
                        {isAdmin && (
                            <Card className="border-0 shadow-sm mb-3 border-start border-primary border-3">
                                <Card.Body>
                                    <Card.Title className="h6 text-primary mb-3">
                                        Admin Actions
                                    </Card.Title>
                                    {actionResult && (
                                        <Alert
                                            variant={actionResult.variant}
                                            className="small py-2 px-3"
                                            dismissible
                                            onClose={() =>
                                                setActionResult(null)
                                            }
                                        >
                                            {actionResult.message}
                                        </Alert>
                                    )}
                                    <div className="d-flex flex-column gap-2">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidate}
                                            disabled={validating || indexing}
                                        >
                                            {validating ? (
                                                <>
                                                    <Spinner
                                                        animation="border"
                                                        size="sm"
                                                        className="me-1"
                                                    />
                                                    Validating...
                                                </>
                                            ) : (
                                                'Validate URL'
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleIndex}
                                            disabled={validating || indexing}
                                        >
                                            {indexing ? (
                                                <>
                                                    <Spinner
                                                        animation="border"
                                                        size="sm"
                                                        className="me-1"
                                                    />
                                                    Indexing...
                                                </>
                                            ) : (
                                                'Re-index'
                                            )}
                                        </Button>
                                        <Link
                                            to="/admin/data-sources"
                                            className="btn btn-link btn-sm text-start text-primary p-0"
                                        >
                                            View Admin Hub
                                        </Link>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Container>
        </section>
    );
}
